<template>
  <AdminLayout>
    <div class="p-6">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-slate-800">1:1 문의 관리</h1>
          <p class="text-sm text-slate-500 mt-1">고객 문의를 확인하고 답변합니다</p>
        </div>
      </div>

      <!-- 통계 카드 -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">전체 문의</p>
          <p class="text-2xl font-bold text-slate-800">{{ totalCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">대기 중</p>
          <p class="text-2xl font-bold text-amber-600">{{ pendingCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">답변 완료</p>
          <p class="text-2xl font-bold text-green-600">{{ answeredCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">답변률</p>
          <p class="text-2xl font-bold text-blue-600">{{ answerRate }}%</p>
        </div>
      </div>

      <!-- 필터 탭 -->
      <div class="flex gap-2 mb-4">
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            statusFilter === null
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter(null)"
        >
          전체
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            statusFilter === 'pending'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('pending')"
        >
          대기 중
        </button>
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            statusFilter === 'answered'
              ? 'bg-green-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="changeFilter('answered')"
        >
          답변 완료
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading && inquiries.length === 0" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>

      <!-- 문의 목록 -->
      <div v-else-if="inquiries.length > 0" class="space-y-3">
        <div
          v-for="inquiry in inquiries"
          :key="inquiry.id"
          class="bg-white rounded-2xl border border-slate-100 overflow-hidden cursor-pointer hover:border-blue-300 transition"
          @click="openDetailModal(inquiry)"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- 상태 -->
              <div
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  inquiry.status === 'pending'
                    ? 'bg-amber-100'
                    : 'bg-green-100'
                ]"
              >
                <svg
                  v-if="inquiry.status === 'pending'"
                  class="w-5 h-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <svg
                  v-else
                  class="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>

              <!-- 내용 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    {{ getCategoryLabel(inquiry.category) }}
                  </span>
                  <span
                    :class="[
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      inquiry.status === 'pending'
                        ? 'bg-amber-100 text-amber-600'
                        : 'bg-green-100 text-green-600'
                    ]"
                  >
                    {{ inquiry.status === 'pending' ? '대기 중' : '답변 완료' }}
                  </span>
                </div>
                <h3 class="font-bold text-slate-800 mb-1 truncate">{{ inquiry.title }}</h3>
                <p class="text-sm text-slate-500 line-clamp-1">{{ getPlainText(inquiry.content) }}</p>
                <div class="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  <span>{{ inquiry.userName }}</span>
                  <span>{{ formatDate(inquiry.createdAt) }}</span>
                </div>
              </div>

              <!-- 화살표 -->
              <svg class="w-5 h-5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- 더 보기 -->
        <button
          v-if="hasMore"
          class="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
          :disabled="loading"
          @click="loadMore"
        >
          {{ loading ? '로딩 중...' : '더 보기' }}
        </button>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="text-center py-12 bg-white rounded-2xl border border-slate-100">
        <span class="text-4xl mb-4 block">💬</span>
        <h3 class="text-lg font-bold text-slate-800 mb-2">문의가 없습니다</h3>
        <p class="text-sm text-slate-500">접수된 문의가 없습니다</p>
      </div>

      <!-- 상세/답변 모달 -->
      <div
        v-if="showDetailModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="closeDetailModal"
      >
        <div class="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div class="p-6 border-b border-slate-100">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-bold text-slate-800">문의 상세</h2>
              <button
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
                @click="closeDetailModal"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="p-6" v-if="selectedInquiry">
            <!-- 문의 정보 -->
            <div class="mb-6">
              <div class="flex items-center gap-2 mb-3">
                <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                  {{ getCategoryLabel(selectedInquiry.category) }}
                </span>
                <span
                  :class="[
                    'px-2 py-0.5 text-xs font-medium rounded-full',
                    selectedInquiry.status === 'pending'
                      ? 'bg-amber-100 text-amber-600'
                      : 'bg-green-100 text-green-600'
                  ]"
                >
                  {{ selectedInquiry.status === 'pending' ? '대기 중' : '답변 완료' }}
                </span>
              </div>
              <h3 class="text-lg font-bold text-slate-800 mb-2">{{ selectedInquiry.title }}</h3>
              <div class="flex items-center gap-3 text-sm text-slate-500 mb-4">
                <span>{{ selectedInquiry.userName }} ({{ selectedInquiry.userEmail }})</span>
                <span>{{ formatDate(selectedInquiry.createdAt) }}</span>
              </div>
              <div
                class="p-4 bg-slate-50 rounded-xl text-sm text-slate-700 leading-relaxed"
                v-html="selectedInquiry.content"
              ></div>

              <!-- 첨부 이미지 -->
              <div v-if="selectedInquiry.images && selectedInquiry.images.length > 0" class="mt-4">
                <p class="text-xs font-semibold text-slate-500 mb-2">첨부 사진</p>
                <div class="grid grid-cols-3 gap-2">
                  <div
                    v-for="(image, idx) in selectedInquiry.images"
                    :key="idx"
                    class="aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer hover:opacity-80 transition"
                    @click="openImageModal(image)"
                  >
                    <img :src="image" alt="첨부 이미지" class="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>

            <!-- 기존 답변 -->
            <div v-if="selectedInquiry.status === 'answered'" class="mb-6">
              <h4 class="text-sm font-semibold text-slate-700 mb-2">답변</h4>
              <div class="p-4 bg-blue-50 rounded-xl text-sm text-slate-700 leading-relaxed">
                <div v-html="selectedInquiry.answer"></div>
                <div class="mt-3 text-xs text-slate-500">
                  {{ formatDate(selectedInquiry.answeredAt) }} | {{ selectedInquiry.answeredBy }}
                </div>
              </div>
            </div>

            <!-- 답변 작성 -->
            <div v-if="selectedInquiry.status === 'pending'" class="space-y-4">
              <h4 class="text-sm font-semibold text-slate-700">답변 작성</h4>
              <div class="wysiwyg-editor">
                <!-- 에디터 툴바 -->
                <div class="editor-toolbar">
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('bold') }"
                      @click="execCommand('bold')"
                      title="굵게"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('italic') }"
                      @click="execCommand('italic')"
                      title="기울임"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('underline') }"
                      @click="execCommand('underline')"
                      title="밑줄"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertUnorderedList')"
                      title="글머리 기호"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertOrderedList')"
                      title="번호 매기기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="insertLink"
                      title="링크 삽입"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- 에디터 영역 -->
                <div
                  ref="answerEditorRef"
                  class="editor-content"
                  contenteditable="true"
                  @input="onAnswerEditorInput"
                  @mouseup="updateToolbarState"
                  @keyup="updateToolbarState"
                ></div>
              </div>

              <button
                class="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50"
                :disabled="!answerContent.trim() || submitting"
                @click="submitAnswer"
              >
                {{ submitting ? '등록 중...' : '답변 등록' }}
              </button>
            </div>

            <!-- 답변 수정 -->
            <div v-else class="mt-4">
              <button
                v-if="!isEditingAnswer"
                class="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
                @click="startEditAnswer"
              >
                답변 수정
              </button>
              <div v-else class="space-y-4">
                <h4 class="text-sm font-semibold text-slate-700">답변 수정</h4>
                <div class="wysiwyg-editor">
                  <div class="editor-toolbar">
                    <div class="toolbar-group">
                      <button type="button" class="toolbar-btn" :class="{ active: isFormatActive('bold') }" @click="execCommand('bold')" title="굵게">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/></svg>
                      </button>
                      <button type="button" class="toolbar-btn" :class="{ active: isFormatActive('italic') }" @click="execCommand('italic')" title="기울임">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/></svg>
                      </button>
                      <button type="button" class="toolbar-btn" :class="{ active: isFormatActive('underline') }" @click="execCommand('underline')" title="밑줄">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/></svg>
                      </button>
                    </div>
                  </div>
                  <div
                    ref="answerEditorRef"
                    class="editor-content"
                    contenteditable="true"
                    @input="onAnswerEditorInput"
                    @mouseup="updateToolbarState"
                    @keyup="updateToolbarState"
                  ></div>
                </div>
                <div class="flex gap-3">
                  <button
                    class="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
                    @click="cancelEditAnswer"
                  >
                    취소
                  </button>
                  <button
                    class="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50"
                    :disabled="!answerContent.trim() || submitting"
                    @click="updateAnswer"
                  >
                    {{ submitting ? '수정 중...' : '수정 완료' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- 삭제 버튼 -->
            <button
              class="w-full mt-4 py-3 text-red-600 font-medium hover:bg-red-50 rounded-xl transition"
              @click="confirmDelete"
            >
              문의 삭제
            </button>
          </div>
        </div>
      </div>

      <!-- 삭제 확인 모달 -->
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="showDeleteConfirm = false"
      >
        <div class="bg-white rounded-3xl w-full max-w-sm p-6 text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-slate-800 mb-2">문의를 삭제할까요?</h3>
          <p class="text-sm text-slate-500 mb-6">삭제된 문의는 복구할 수 없습니다.</p>
          <div class="flex gap-3">
            <button
              class="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
              @click="showDeleteConfirm = false"
            >
              취소
            </button>
            <button
              class="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition"
              @click="handleDelete"
            >
              삭제
            </button>
          </div>
        </div>
      </div>

      <!-- 이미지 확대 모달 -->
      <div
        v-if="showImageModal"
        class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90"
        @click="showImageModal = false"
      >
        <div class="relative max-w-full max-h-full" @click.stop>
          <button
            class="absolute -top-10 right-0 text-white hover:text-slate-300"
            @click="showImageModal = false"
          >
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          <img :src="modalImage" alt="이미지 확대" class="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      </div>

      <!-- 토스트 -->
      <transition name="toast">
        <div
          v-if="showToast"
          :class="[
            'fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-lg z-50',
            toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          ]"
        >
          {{ toastMessage }}
        </div>
      </transition>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useInquiry, INQUIRY_CATEGORIES } from '@/composables/common/useInquiry'
import { useAuth } from '@/composables/common/useAuth'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'InquiryManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { loading, getAllInquiries, answerInquiry, updateInquiry, deleteInquiry } = useInquiry()
    const { user } = useAuth()

    const inquiries = ref([])
    const lastDoc = ref(null)
    const hasMore = ref(false)
    const statusFilter = ref(null)

    // 통계
    const totalCount = ref(0)
    const pendingCount = ref(0)
    const answeredCount = ref(0)

    // 상세 모달
    const showDetailModal = ref(false)
    const selectedInquiry = ref(null)
    const answerContent = ref('')
    const submitting = ref(false)
    const isEditingAnswer = ref(false)

    // WYSIWYG 에디터
    const answerEditorRef = ref(null)
    const activeFormats = ref({
      bold: false,
      italic: false,
      underline: false
    })

    // 삭제 확인
    const showDeleteConfirm = ref(false)

    // 이미지 모달
    const showImageModal = ref(false)
    const modalImage = ref('')

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')
    const toastType = ref('success')

    // 답변률
    const answerRate = computed(() => {
      if (totalCount.value === 0) return 0
      return Math.round((answeredCount.value / totalCount.value) * 100)
    })

    // 카테고리 라벨
    const getCategoryLabel = (categoryId) => {
      const cat = INQUIRY_CATEGORIES.find(c => c.id === categoryId)
      return cat ? `${cat.icon} ${cat.label}` : categoryId
    }

    // HTML에서 텍스트 추출
    const getPlainText = (html) => {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '')
    }

    // 날짜 포맷
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 토스트 표시
    const showToastMessage = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000)
    }

    // WYSIWYG 에디터: execCommand 실행
    const execCommand = (command, value = null) => {
      answerEditorRef.value?.focus()
      document.execCommand(command, false, value)
      updateToolbarState()
      syncAnswerContent()
    }

    // 포맷 활성 상태 확인
    const isFormatActive = (format) => {
      return activeFormats.value[format]
    }

    // 툴바 상태 업데이트
    const updateToolbarState = () => {
      activeFormats.value.bold = document.queryCommandState('bold')
      activeFormats.value.italic = document.queryCommandState('italic')
      activeFormats.value.underline = document.queryCommandState('underline')
    }

    // 답변 내용 동기화
    const syncAnswerContent = () => {
      if (answerEditorRef.value) {
        answerContent.value = answerEditorRef.value.innerHTML
      }
    }

    // 에디터 입력 이벤트
    const onAnswerEditorInput = () => {
      syncAnswerContent()
    }

    // 링크 삽입
    const insertLink = () => {
      const selection = window.getSelection()
      const selectedText = selection.toString() || '링크'
      const url = prompt('링크 URL을 입력하세요:', 'https://')
      if (!url) return

      if (selection.toString()) {
        execCommand('createLink', url)
      } else {
        const linkHtml = `<a href="${url}" target="_blank">${selectedText}</a>`
        execCommand('insertHTML', linkHtml)
      }
    }

    // 문의 목록 로드
    const loadInquiries = async () => {
      const result = await getAllInquiries(20, null, statusFilter.value)
      inquiries.value = result.inquiries
      lastDoc.value = result.lastDoc
      hasMore.value = result.hasMore
      await loadStats()
    }

    // 통계 로드
    const loadStats = async () => {
      const allResult = await getAllInquiries(1000, null, null)
      totalCount.value = allResult.inquiries.length
      pendingCount.value = allResult.inquiries.filter(i => i.status === 'pending').length
      answeredCount.value = allResult.inquiries.filter(i => i.status === 'answered').length
    }

    // 더 보기
    const loadMore = async () => {
      if (!hasMore.value || loading.value) return
      const result = await getAllInquiries(20, lastDoc.value, statusFilter.value)
      inquiries.value = [...inquiries.value, ...result.inquiries]
      lastDoc.value = result.lastDoc
      hasMore.value = result.hasMore
    }

    // 필터 변경
    const changeFilter = async (filter) => {
      statusFilter.value = filter
      await loadInquiries()
    }

    // 상세 모달 열기
    const openDetailModal = (inquiry) => {
      selectedInquiry.value = inquiry
      answerContent.value = ''
      isEditingAnswer.value = false
      showDetailModal.value = true
      nextTick(() => {
        if (answerEditorRef.value) {
          answerEditorRef.value.innerHTML = ''
        }
      })
    }

    // 상세 모달 닫기
    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedInquiry.value = null
      answerContent.value = ''
      isEditingAnswer.value = false
    }

    // 답변 등록
    const submitAnswer = async () => {
      if (!answerContent.value.trim() || !selectedInquiry.value) return

      submitting.value = true
      try {
        await answerInquiry(
          selectedInquiry.value.id,
          answerContent.value,
          user.value?.email || 'admin'
        )
        showToastMessage('답변이 등록되었습니다')
        closeDetailModal()
        await loadInquiries()
      } catch (err) {
        showToastMessage('답변 등록 중 오류가 발생했습니다', 'error')
      } finally {
        submitting.value = false
      }
    }

    // 답변 수정 시작
    const startEditAnswer = () => {
      isEditingAnswer.value = true
      answerContent.value = selectedInquiry.value.answer
      nextTick(() => {
        if (answerEditorRef.value) {
          answerEditorRef.value.innerHTML = selectedInquiry.value.answer
        }
      })
    }

    // 답변 수정 취소
    const cancelEditAnswer = () => {
      isEditingAnswer.value = false
      answerContent.value = ''
    }

    // 답변 수정
    const updateAnswer = async () => {
      if (!answerContent.value.trim() || !selectedInquiry.value) return

      submitting.value = true
      try {
        await updateInquiry(selectedInquiry.value.id, {
          answer: answerContent.value,
          answeredAt: new Date().toISOString(),
          answeredBy: user.value?.email || 'admin'
        })
        showToastMessage('답변이 수정되었습니다')
        closeDetailModal()
        await loadInquiries()
      } catch (err) {
        showToastMessage('답변 수정 중 오류가 발생했습니다', 'error')
      } finally {
        submitting.value = false
      }
    }

    // 삭제 확인
    const confirmDelete = () => {
      showDeleteConfirm.value = true
    }

    // 삭제 처리
    const handleDelete = async () => {
      if (!selectedInquiry.value) return

      try {
        await deleteInquiry(selectedInquiry.value.id)
        showToastMessage('문의가 삭제되었습니다')
        showDeleteConfirm.value = false
        closeDetailModal()
        await loadInquiries()
      } catch (err) {
        showToastMessage('삭제 중 오류가 발생했습니다', 'error')
      }
    }

    // 이미지 모달 열기
    const openImageModal = (imageUrl) => {
      modalImage.value = imageUrl
      showImageModal.value = true
    }

    onMounted(() => {
      loadInquiries()
    })

    return {
      loading,
      inquiries,
      hasMore,
      statusFilter,
      totalCount,
      pendingCount,
      answeredCount,
      answerRate,
      showDetailModal,
      selectedInquiry,
      answerContent,
      submitting,
      isEditingAnswer,
      showDeleteConfirm,
      showImageModal,
      modalImage,
      showToast,
      toastMessage,
      toastType,
      answerEditorRef,
      getCategoryLabel,
      getPlainText,
      formatDate,
      loadMore,
      changeFilter,
      openDetailModal,
      closeDetailModal,
      submitAnswer,
      startEditAnswer,
      cancelEditAnswer,
      updateAnswer,
      confirmDelete,
      handleDelete,
      openImageModal,
      // WYSIWYG
      execCommand,
      isFormatActive,
      updateToolbarState,
      onAnswerEditorInput,
      insertLink
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

/* WYSIWYG 에디터 */
.wysiwyg-editor {
  @apply border border-slate-200 rounded-xl overflow-hidden bg-white;
}

.editor-toolbar {
  @apply flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200;
}

.toolbar-group {
  @apply flex items-center gap-0.5;
}

.toolbar-btn {
  @apply w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-white hover:text-blue-600 rounded-lg transition;
}

.toolbar-btn.active {
  @apply bg-blue-100 text-blue-600;
}

.toolbar-divider {
  @apply w-px h-6 bg-slate-200 mx-1;
}

.editor-content {
  @apply min-h-[150px] max-h-[250px] overflow-y-auto p-4 text-sm text-slate-700 leading-relaxed outline-none;
}

.editor-content:empty::before {
  content: '답변 내용을 입력하세요...';
  @apply text-slate-400;
}

.editor-content:focus {
  @apply bg-blue-50/30;
}

/* 토스트 애니메이션 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
